import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DropdownContainer } from 'shared/components/Dropdown/Dropdown';
import { DatePickerContainer } from 'shared/components/DatePicker';
import { HeaderTabs, Icon } from '@ihsm/ui-shared';
import { PageHeader } from 'shared/components/PageHeader';
import { HeaderTab } from '@ihsm/ui-shared/lib/typings/shared/components/HeaderTabs';
import './HeaderControls.scss';

import {
  PORTFOLIOS_BENCHMARKS,
  PORTFOLIOS,
  CUSTOM_REPORT,
  PORTFOLIOS_MAPPINGS,
} from 'routes';
import { useAppSelector, useActions } from 'state/store';
import { formatDateForURL } from 'shared/utils';
import { BenchmarkDropdown } from './BenchmarkDropdown';
import { FundDropdown } from './FundDropdown';
import { REPORTS_HEADER_ITEMS } from './constants';

interface HeaderControlsProps {
  headerTabItems?: HeaderTab[];
  disableFiltersArea?: boolean;
  children?: React.ReactNode;
}

export const HeaderControls: React.FC<HeaderControlsProps> = ({
  children,
  headerTabItems = REPORTS_HEADER_ITEMS,
  disableFiltersArea = false,
}: HeaderControlsProps): JSX.Element => {
  const { selectedValuationDate, valuationDates } = useAppSelector(
    (state) => state.valuationDatesState
  );
  const { selectedFund, fundsList } = useAppSelector(
    (state) => state.fundsState
  );
  const { selectedBenchmark, benchmarkList } = useAppSelector(
    (state) => state.benchmarkState
  );
  const { setSelectedValuationDate, setSelectedFund, setSelectedBenchmark } =
    useActions();
  const { pathname } = useLocation();

  const isPortfoliosPage = pathname.startsWith(PORTFOLIOS);
  const isBenchmarkSelected = pathname === PORTFOLIOS_BENCHMARKS;
  const isMappingsSelected = pathname === PORTFOLIOS_MAPPINGS;
  const showFundDropDown = !isBenchmarkSelected && !isMappingsSelected;
  const showBenchmarkDropDown =
    (isPortfoliosPage && isBenchmarkSelected) || !isPortfoliosPage;
  const showCurrencyDropDown = isPortfoliosPage && !isMappingsSelected;
  const isBenchmarkDisabled = pathname.startsWith(CUSTOM_REPORT);

  const contextChangeHandler = (benchmark: string) => {
    if (selectedBenchmark !== benchmark) {
      setSelectedBenchmark(benchmark);
    }
  };

  const fundChangeHandler = (fund: string) => {
    if (selectedFund !== fund) {
      setSelectedFund(fund);
    }
  };

  const valuationDateChangeHandler = (date: Date) => {
    const dateFormatted = formatDateForURL(date);
    if (selectedValuationDate !== dateFormatted) {
      setSelectedValuationDate(dateFormatted);
    }
  };

  const valuationDatesMapped = useMemo(
    () => valuationDates?.map((date) => new Date(date)),
    [valuationDates]
  );

  return (
    <PageHeader>
      <div className="header-labels">
        <Icon source="BuysideLogo" />
      </div>
      <div className="header-control-items">
        <HeaderTabs items={headerTabItems} />
        <div className="header-selection-controls">
          {!disableFiltersArea ? (
            <>
              {!isPortfoliosPage && (
                <DatePickerContainer
                  valuationDate={new Date(selectedValuationDate)}
                  valuationDateList={valuationDatesMapped}
                  onChange={valuationDateChangeHandler}
                />
              )}

              {showFundDropDown && (
                <FundDropdown
                  selected={selectedFund}
                  options={fundsList}
                  onChange={fundChangeHandler}
                />
              )}

              {showBenchmarkDropDown && (
                <BenchmarkDropdown
                  selected={selectedBenchmark}
                  options={benchmarkList}
                  onChange={contextChangeHandler}
                  isDisabled={isBenchmarkDisabled}
                />
              )}

              {showCurrencyDropDown && (
                <DropdownContainer
                  labelText="Currency"
                  value="USD"
                  options={['USD']}
                  onChange={() => {}}
                  disabled
                  verticalLayout
                />
              )}

              {isPortfoliosPage && (
                <DropdownContainer
                  labelText="Loaded"
                  value="03-Sep-2021 7:50"
                  options={['03-Sep-2021 7:50']}
                  onChange={() => {}}
                  disabled
                  verticalLayout
                />
              )}
            </>
          ) : null}
          {children ? <div className="children-wrapper">{children}</div> : null}
        </div>
      </div>
    </PageHeader>
  );
};
